
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import Typography, { TypographyProps } from '@mui/material/Typography';
import classNames from 'classnames';

export interface IProps {
  fullScreen?: boolean;
  /** Content of the dialog title */
  title?: string;
  /** These props will be forwarded to the title */
  titleTypographyProps?: TypographyProps;
  /** Icon that will be displayed after the title*/
  titleIcon?: React.ReactNode;
  /** Icon that will be displayed in the header area to the left*/
  headerIcon?: React.ReactNode;
  /** Content of the dialog subtitle */
  subtitle?: React.ReactNode;
  /** These props will be forwarded to the subtitle */
  subtitleTypographyProps?: TypographyProps;
  /** The tools to display in the dialog header */
  tools?: React.ReactNode;
  /** The area to display in the bottom of the dialog header */
  bottomArea?: React.ReactNode;

  topToolbarArea?: React.ReactNode;

  className?: string;

  selectionMenu?: boolean;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  defaultCursor?: boolean;
}

const styles = (theme: Theme) => {
  return {
    /** Styles applied to the root element */
    root: {
      margin: theme.spacing(0),
      padding: theme.spacing(3, 3, 2.5),
      flex: '0 0 auto',
      background: theme.customProperties.Dialog.headerBg,
      color: theme.customProperties.Dialog.headerColor,
    },
    cursor: {
      cursor: 'move',
    },
    /** Styles applied to the content wrapper element */
    content: {
      cursor: 'default',
      // minWidth: theme.spacing(35), This brakes the dialog for phone users
    },
    /** Styles applied to the tools element */
    tools: {
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
      paddingRight: theme.spacing(2),
    },
    /** Styles applied to the title Typography element */
    title: {
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
      display: 'inline',
    },
    defaultCursor: {
      cursor: 'default',
    },
    /** Styles applied to the subheader Typography element */
    subtitle: {
      marginTop: theme.spacing(1),
      color: theme.customProperties.Fade.LightOnDark.mediumEmphasis,
    },
    /** Styles applied to the content and tools wrapper element */
    topArea: {
      padding: theme.spacing(0),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    /** Styles applied to the bottom area element */
    bottomArea: {
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
      display: 'flex',
      '& p': {
        cursor: 'default',
      },
      '& div': {
        cursor: 'default',
      },
      marginTop: theme.spacing(1.25),
    },
    topToolbar: {
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
      display: 'flex',
      '& p': {
        cursor: 'default',
      },
      '& div': {
        cursor: 'default',
      },
      marginTop: theme.spacing(1.25),
      [theme.breakpoints.down('md')]: {
        marginTop: 0,
      },
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
  };
};

const EDDialogHeader: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const {
    className,
    title,
    subtitle,
    titleTypographyProps,
    subtitleTypographyProps,
    tools,
    bottomArea,
    topToolbarArea,
  } = props;
  const classes = withStyles.getClasses(props);
  /*   console.log(bottomArea.props.children[1].props.className);
   */
  return (
    <div
      className={classNames(classes.root, className, 'draggable', {
        [classes.cursor]: !props.fullScreen,
        [classes.defaultCursor]: props.defaultCursor,
      })}
    >
      <div className={classNames(classes.topArea)}>
        {props.headerIcon}
        <div className={classNames(classes.content)}>
          <span
            className={props.titleIcon ? classes.titleContainer : undefined}
          >
            <Typography
              variant="h6"
              className={classNames(classes.title)}
              {...titleTypographyProps}
            >
              {title}
            </Typography>
            {props.titleIcon}
          </span>
          {subtitle && (
            <div>
              <Typography
                variant="body2"
                color="inherit"
                component="span"
                className={classNames(classes.subtitle)}
                {...subtitleTypographyProps}
              >
                {subtitle}
              </Typography>
            </div>
          )}
        </div>
        {tools && <div className={classNames(classes.tools)}>{tools}</div>}
        {topToolbarArea && (
          <div className={classNames(classes.topToolbar)}>{topToolbarArea}</div>
        )}
      </div>
      {bottomArea && (
        <div className={classNames(classes.bottomArea)}>{bottomArea}</div>
      )}
    </div>
  );
};

export default withStyles(EDDialogHeader, styles);
